import { AddProductToWishListRequest } from '../interfaces/wish-list-update-product.interface';
import { ProductCardData } from '../../product-card/product-card-display';
import { RemoveProductFromWishListRequest } from '../interfaces/wish-list-update-product.interface';
import { WishList, WishListItem, WishLists } from '../interfaces/wish-list-items.interface';
import { buildSkuFromIsbn, getPrimaryIsbn } from '../../../common/utils/product-util';
import { html, TemplateResult } from 'lit';
import { loading } from '../../../common/constants/api-constants';
import { mapWishListItemToProductCardData } from '../mapping/wish-list-product-card.mapping';

/**
 * Gets the request to add a product to a wish list.
 *
 * @param comment
 * @param product
 * @param wishlistId
 */
export function getAddProductToWishListRequest(
  comment: string,
  product?: ProductCardData,
  wishlistId?: number
): AddProductToWishListRequest {
  return {
    wishlistId: wishlistId || 0,
    sku: buildSkuFromIsbn(getPrimaryIsbn(product?.isbn13, product?.isbn, product?.mhid), product?.businessUnit ?? ''),
    qty: 1,
    comments: comment,
  }
}

/**
 * Build the request to remove a product from a wish list.
 *
 * @param wishList
 * @param wishListItemId
 */
export function getRemoveProductFromWishListRequest(wishList: WishList, wishListItemId: number): RemoveProductFromWishListRequest {
  return {
    wishListId: wishList.wishlist_id,
    wishListItemId: wishListItemId,
  };
}

/**
 * Retrieves a product from a wish list.
 *
 * @param product
 * @param wishList
 */
export function getProductFromWishList(product?: ProductCardData, wishList?: WishList): WishListItem | undefined {
  if (!product || !wishList) {
    return undefined;
  }

  return wishList?.wishlist_items?.find((item) => item.isbn13 === product?.isbn13);
}

/**
 * Determines if a product is in a wish list.
 *
 * @param product
 * @param wishList
 */
export function isProductInWishList(product?: ProductCardData, wishList?: WishList): boolean {
  if (!product || !wishList) {
    return false;
  }

  return wishList.wishlist_items && wishList.wishlist_items.some((item) => item.isbn13 === product?.isbn13);
}

/**
 * Determines if a product is in any wish list.
 *
 * @param product
 * @param wishLists
 */
export function isProductInWishLists(product?: ProductCardData, wishLists?: WishLists): boolean {
  if (!product || !wishLists || !wishLists.items) {
    return false;
  }

  return wishLists.items.some((wishList: WishList) => isProductInWishList(product, wishList));
}

/**
 * Renders a loading template for the product in the wish-list off-canvas.
 */
export function renderOffCanvasProductLoading(): TemplateResult {
  return html`
    <span class="visually-hidden">Loading product in wish list off-canvas</span>
    ${loading({ columns: 3 })}
  `;
}

export function mapWishListItemsToProducts(items: WishListItem[], loggedInUserName?: string): ProductCardData[] {
  if (!items?.length) {
    return [];
  }
  return items.map((item) => mapWishListItemToProductCardData(item, loggedInUserName));
}
